import { Dispatch, type HTMLAttributes, SetStateAction } from 'react';
import { Button, type ButtonProps } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import React from 'react';
import { type TypeofInputs } from '@/lib/types';
import { cn } from '@/lib/utils';

type ButtonOrientation = 'right' | 'bottom';

interface InputButton extends ButtonProps {
	orientation: ButtonOrientation;
}

export interface FoundationInputProps extends HTMLAttributes<HTMLInputElement> {
	button?: InputButton;
	label?: string;
	hint?: string;
	id: string;
	className?: string;
	placeholder: string;
	type: TypeofInputs;
	setInputState?: Dispatch<SetStateAction<string | undefined>>;
	inputState?: string;
	disabled?: boolean;
	ref?: React.RefObject<HTMLInputElement>;
}

const FoundationInput = React.forwardRef<
	HTMLInputElement,
	FoundationInputProps
>(
	(
		{
			className,
			button,
			label,
			hint,
			id,
			placeholder,
			type,
			setInputState,
			inputState,
			disabled = false,
			...rest
		},
		ref
	) => {
		const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			setInputState?.(event.target.value);
		};

		return (
			<div className='wlcb-flex wlcb-flex-col wlcb-gap-y-2'>
				{label ? (
					<Label
						style={{ color: 'var(--secondary-primary)' }}
						className='wlcb-text-caption wlcb-font-[400] '
						htmlFor={id}
					>
						{label}
					</Label>
				) : null}
				<div
					className={cn(
						`wlcb-flex wlcb-w-full wlcb-items-start wlcb-max-w-sm wlcb-space-x-2 ${className}`,
						{
							'wlcb-flex-col wlcb-items-start wlcb-gap-y-2':
								button?.orientation === 'bottom',
						}
					)}
				>
					<div className='wlcb-flex wlcb-flex-col wlcb-gap-y-2 wlcb-w-full'>
						<Input
							{...rest}
							ref={ref}
							disabled={disabled}
							aria-disabled={disabled}
							value={inputState ?? ''}
							onChange={handleChange}
							type={type}
							placeholder={placeholder}
							className={cn(
								'wlcb-bg-surface wlcb-text-body wlcb-border-stroke-subtle hover:wlcb-border-stroke-subtle-hover',
								{
									'wlcb-border-stroke-strong wlcb-text-text-primary':
										inputState,
								}
							)}
						/>
						{hint ? (
							<small className='wlcb-text-left wlcb-text-caption wlcb-text-text-hint wlcb-max-w-prose'>
								{hint}
							</small>
						) : null}
					</div>
					{button ? (
						<Button
							disabled={disabled}
							aria-disabled={disabled}
							onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
								button?.onClick?.(e)
							}
							className={
								cn({
									'!wlcb-ml-0': button.orientation === 'bottom',
								}) + ` ${button.className ? button.className : ''}`
							}
							variant={button.variant}
							size={button.size ?? 'md'}
							type={button.type}
							name={button.name}
							icon={
								button.icon
									? {
											iconType: button.icon.iconType,
											orientation: button.icon.orientation,
										}
									: undefined
							}
						/>
					) : null}
				</div>
			</div>
		);
	}
);

FoundationInput.displayName = 'FoundationInput';

export default FoundationInput;
